import React, { useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Link } from 'react-router-dom';
import { useLocale } from '../../contexts/LocaleContext';
import { API_BASE } from '../../config';
import { scrollAppToTop } from '../../utils/scroll-app-main';
import { AdminAccessDenied } from './AdminAccessDenied';
import {
  ADMIN_FILTER_BAR_CLASSNAME,
  ADMIN_INLINE_SELECT_CLASSNAME,
  ADMIN_LABEL_CLASSNAME,
  ADMIN_MUTED_TEXT_CLASSNAME,
  ADMIN_PAGE_NARROW_CLASSNAME,
  AdminEmptyState,
  AdminErrorAlert,
  AdminLoadingState,
  AdminPaginationBar,
  AdminTableWrap,
} from './admin-ui';

type EventType =
  | 'user_clicked_plan'
  | 'user_requested_plan_change'
  | 'user_signed_up'
  | 'signup_invitation_code_failed'
  | 'tier_limit_modal_shown'
  | 'credit_limit_modal_shown'
  | 'user_clicked_buy_credits_1000'
  | 'user_clicked_buy_credits_5000'
  | 'user_clicked_buy_credits_15000'
  | 'credit_purchase_effective_1000'
  | 'credit_purchase_effective_5000'
  | 'credit_purchase_effective_15000'
  | 'style_notes_regenerated';

interface ObservabilityEvent {
  id: string;
  createdAt: string;
  eventType: EventType;
  userId: string | null;
  userMessage: string | null;
  userEmail?: string;
}

const EVENTS_PER_PAGE = 20;

export const ObservabilityEventsAdmin: React.FC = () => {
  const { user, token, masterKey } = useAuth();
  const { t } = useLocale();
  const [events, setEvents] = useState<ObservabilityEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filterType, setFilterType] = useState<EventType | 'all'>('all');
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    const fetchEvents = async () => {
      if ((!token && !masterKey) || !user?.admin) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const url = masterKey
          ? `${API_BASE}/api/admin/observability-events?masterKey=${encodeURIComponent(masterKey)}`
          : `${API_BASE}/api/admin/observability-events`;
        const response = await fetch(url, {
          headers: {
            ...(token && !masterKey ? { Authorization: `Bearer ${token}` } : {}),
          },
        });

        if (!response.ok) {
          throw new Error('Failed to fetch observability events');
        }

        const data = await response.json();
        setEvents(data.events || []);
        setError(null);
      } catch (err) {
        console.error('Error fetching observability events:', err);
        setError('Failed to load observability events');
      } finally {
        setLoading(false);
      }
    };

    fetchEvents();
  }, [token, user, masterKey, t]);

  if (!user || !user.admin) {
    return <AdminAccessDenied />;
  }

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  const getEventTypeLabel = (eventType: string): string => {
    switch (eventType) {
      case 'user_clicked_plan':
        return 'User clicked plan';
      case 'user_requested_plan_change':
        return 'User requested plan change';
      case 'user_signed_up':
        return 'User signed up';
      case 'signup_invitation_code_failed':
        return 'Signup invitation code failed';
      case 'tier_limit_modal_shown':
        return t('tier_limit_modal_shown') || 'Tier limit modal shown';
      case 'credit_limit_modal_shown':
        return t('credit_limit_modal_shown') || 'Credit limit modal shown';
      case 'user_clicked_buy_credits_1000':
        return 'User clicked buy credits (1,000)';
      case 'user_clicked_buy_credits_5000':
        return 'User clicked buy credits (5,000)';
      case 'user_clicked_buy_credits_15000':
        return 'User clicked buy credits (15,000)';
      case 'credit_purchase_effective_1000':
        return 'Credit purchase effective (1,000)';
      case 'credit_purchase_effective_5000':
        return 'Credit purchase effective (5,000)';
      case 'credit_purchase_effective_15000':
        return 'Credit purchase effective (15,000)';
      case 'onboarding_achievement_unlocked':
        return 'Onboarding achievement unlocked';
      case 'onboarding_reward_unlocked':
        return 'Onboarding reward unlocked';
      case 'stripe_credit_purchase_completed':
        return 'Stripe credit purchase completed';
      case 'style_notes_regenerated':
        return 'Style notes regenerated';
      default:
        return eventType;
    }
  };

  const filteredEvents = filterType === 'all'
    ? events
    : events.filter(event => event.eventType === filterType);

  const totalPages = Math.ceil(filteredEvents.length / EVENTS_PER_PAGE);
  const startIndex = (currentPage - 1) * EVENTS_PER_PAGE;
  const endIndex = startIndex + EVENTS_PER_PAGE;
  const paginatedEvents = filteredEvents.slice(startIndex, endIndex);

  const handleFilterChange = (newFilter: EventType | 'all') => {
    setFilterType(newFilter);
    setCurrentPage(1);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    scrollAppToTop('smooth');
  };

  return (
    <>
      <div className={ADMIN_PAGE_NARROW_CLASSNAME}>
        <div className={`${ADMIN_FILTER_BAR_CLASSNAME} mb-6 justify-start`}>
          <label className={ADMIN_LABEL_CLASSNAME}>
            Filter by type:
          </label>
          <select
            value={filterType}
            onChange={(e) => handleFilterChange(e.target.value as EventType | 'all')}
            className={ADMIN_INLINE_SELECT_CLASSNAME}
          >
            <option value="all">All</option>
            <option value="user_signed_up">User signed up</option>
            <option value="signup_invitation_code_failed">Signup invitation code failed</option>
            <option value="user_clicked_plan">User clicked plan</option>
            <option value="user_requested_plan_change">User requested plan change</option>
            <option value="tier_limit_modal_shown">{t('tier_limit_modal_shown') || 'Tier limit modal shown'}</option>
            <option value="credit_limit_modal_shown">{t('credit_limit_modal_shown') || 'Credit limit modal shown'}</option>
            <option value="user_clicked_buy_credits_1000">User clicked buy credits (1,000)</option>
            <option value="user_clicked_buy_credits_5000">User clicked buy credits (5,000)</option>
            <option value="user_clicked_buy_credits_15000">User clicked buy credits (15,000)</option>
            <option value="credit_purchase_effective_1000">Credit purchase effective (1,000)</option>
            <option value="credit_purchase_effective_5000">Credit purchase effective (5,000)</option>
            <option value="credit_purchase_effective_15000">Credit purchase effective (15,000)</option>
            <option value="style_notes_regenerated">Style notes regenerated</option>
          </select>
          <span className={ADMIN_MUTED_TEXT_CLASSNAME}>
            Showing {filteredEvents.length} event{filteredEvents.length !== 1 ? 's' : ''}
          </span>
        </div>

        {error && (
          <div className="mb-4">
            <AdminErrorAlert>{error}</AdminErrorAlert>
          </div>
        )}

        {loading ? (
          <AdminLoadingState />
        ) : filteredEvents.length === 0 ? (
          <AdminEmptyState>
            No observability events{filterType !== 'all' ? ` for type "${getEventTypeLabel(filterType)}"` : ''}
          </AdminEmptyState>
        ) : (
          <AdminTableWrap>
            <table className="admin-data-table min-w-[700px]">
              <thead>
                <tr>
                  <th>
                    Date
                  </th>
                  <th>
                    Event Type
                  </th>
                  <th>
                    User
                  </th>
                  <th>
                    Message
                  </th>
                </tr>
              </thead>
              <tbody>
                {paginatedEvents.map((event) => (
                  <tr key={event.id}>
                    <td>
                      {formatDate(event.createdAt)}
                    </td>
                    <td>
                      {getEventTypeLabel(event.eventType)}
                    </td>
                    <td>
                      {event.userId ? (
                        <Link
                          to={`/admin/users/${event.userId}`}
                          className="admin-table-link"
                        >
                          {event.userEmail || event.userId}
                        </Link>
                      ) : (
                        'Anonymous'
                      )}
                    </td>
                    <td className="max-w-[400px] break-words">
                      {event.userMessage || '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </AdminTableWrap>
        )}

        {!loading && filteredEvents.length > 0 && (
          <AdminPaginationBar
            page={currentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
          />
        )}
      </div>
    </>
  );
};
